import { EQUIPMENT_KINDS, type EquipmentKind } from '@fewerbags/schema';
import { EQUIPMENT_LABEL } from '../../lib/format';
import {
  emptyStepRow,
  ingredientRefToken,
  moveItem,
  withStepDuration,
  withStepTimer,
  type IngredientRow,
  type OvenMode,
  type StepRow,
} from '../../pages/recipes/recipe-form';
import { RowControls } from './RowControls';

const OVEN_MODES: OvenMode[] = ['bake', 'roast', 'broil', 'convection'];

export function StepRowsEditor({
  rows,
  ingredientRows,
  onChange,
}: {
  rows: StepRow[];
  /** Current ingredient rows, for the "Uses: …" link picker. */
  ingredientRows: IngredientRow[];
  onChange: (rows: StepRow[]) => void;
}) {
  function patch(index: number, next: Partial<StepRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...next } : row)));
  }

  function replace(index: number, next: StepRow) {
    onChange(rows.map((row, i) => (i === index ? next : row)));
  }

  function toggleUses(index: number, token: string, checked: boolean) {
    const row = rows[index]!;
    patch(index, {
      refIngredientIds: checked
        ? [...row.refIngredientIds, token]
        : row.refIngredientIds.filter((t) => t !== token),
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <ol className="flex flex-col gap-3">
        {rows.map((row, i) => (
          <li key={row.key} className="rounded-lg border border-line p-3">
            <div className="flex items-start gap-2">
              <span
                aria-hidden
                className="mt-1 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-semibold text-brand"
              >
                {i + 1}
              </span>
              <div className="flex flex-1 flex-col gap-1">
                <label htmlFor={`step-${row.key}`} className="sr-only">
                  Step {i + 1}
                </label>
                <textarea
                  id={`step-${row.key}`}
                  rows={2}
                  value={row.text}
                  placeholder="What happens in this step?"
                  onChange={(e) => patch(i, { text: e.target.value })}
                  className="rounded-lg border border-line bg-surface p-3 text-base text-ink placeholder:text-muted"
                />
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <div className="flex w-28 flex-col gap-1">
                <label htmlFor={`dur-${row.key}`} className="text-xs font-medium text-muted">
                  Minutes
                </label>
                <input
                  id={`dur-${row.key}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={10080}
                  value={row.durationText}
                  onChange={(e) => replace(i, withStepDuration(row, e.target.value))}
                  className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                />
              </div>
              <fieldset className="flex flex-wrap items-center gap-3 border-0 p-0">
                <legend className="sr-only">Step {i + 1} options</legend>
                <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={row.attended}
                    onChange={(e) => patch(i, { attended: e.target.checked })}
                    className="size-5 accent-[var(--color-brand)]"
                  />
                  Hands-on
                </label>
                <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={row.timer}
                    onChange={(e) => replace(i, withStepTimer(row, e.target.checked))}
                    className="size-5 accent-[var(--color-brand)]"
                  />
                  Timer
                </label>
                <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={row.canDoAhead}
                    onChange={(e) => patch(i, { canDoAhead: e.target.checked })}
                    className="size-5 accent-[var(--color-brand)]"
                  />
                  Can do ahead
                </label>
              </fieldset>
              <RowControls
                label={`step ${i + 1}`}
                index={i}
                total={rows.length}
                onMove={(to) => onChange(moveItem(rows, i, to))}
                onRemove={() => onChange(rows.filter((_, j) => j !== i))}
              />
            </div>

            <div className="mt-2 flex flex-wrap items-end gap-2">
              <div className="flex w-40 flex-col gap-1">
                <label htmlFor={`eq-${row.key}`} className="text-xs font-medium text-muted">
                  Equipment
                </label>
                <select
                  id={`eq-${row.key}`}
                  value={row.equipmentKind}
                  onChange={(e) =>
                    patch(i, { equipmentKind: e.target.value as '' | EquipmentKind })
                  }
                  className="min-h-11 rounded-lg border border-line bg-surface px-2 text-base text-ink"
                >
                  <option value="">None</option>
                  {EQUIPMENT_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {EQUIPMENT_LABEL[kind] ?? kind}
                    </option>
                  ))}
                </select>
              </div>
              {row.equipmentKind === '' ? null : (
                <div className="flex w-20 flex-col gap-1">
                  <label htmlFor={`eqc-${row.key}`} className="text-xs font-medium text-muted">
                    How many
                  </label>
                  <input
                    id={`eqc-${row.key}`}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={20}
                    value={row.equipmentCount}
                    onChange={(e) => patch(i, { equipmentCount: e.target.value })}
                    className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                  />
                </div>
              )}
              {row.equipmentKind === 'oven' ? (
                <>
                  <div className="flex w-24 flex-col gap-1">
                    <label htmlFor={`temp-${row.key}`} className="text-xs font-medium text-muted">
                      Oven °F
                    </label>
                    <input
                      id={`temp-${row.key}`}
                      type="number"
                      inputMode="numeric"
                      min={150}
                      max={600}
                      value={row.ovenTempF}
                      onChange={(e) => patch(i, { ovenTempF: e.target.value })}
                      className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                    />
                  </div>
                  <div className="flex w-36 flex-col gap-1">
                    <label htmlFor={`mode-${row.key}`} className="text-xs font-medium text-muted">
                      Oven mode
                    </label>
                    <select
                      id={`mode-${row.key}`}
                      value={row.ovenMode}
                      onChange={(e) => patch(i, { ovenMode: e.target.value as '' | OvenMode })}
                      className="min-h-11 rounded-lg border border-line bg-surface px-2 text-base text-ink"
                    >
                      <option value="">Unspecified</option>
                      {OVEN_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m[0]!.toUpperCase() + m.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : null}
            </div>

            {ingredientRows.length > 0 ? (
              <details className="mt-2">
                <summary className="min-h-9 cursor-pointer text-xs font-medium text-muted">
                  Uses:{' '}
                  {row.refIngredientIds.length === 0
                    ? 'none linked'
                    : ingredientRows
                        .filter((ing) => row.refIngredientIds.includes(ingredientRefToken(ing)))
                        .map((ing) => ing.name.trim())
                        .filter(Boolean)
                        .join(', ') || 'none linked'}
                </summary>
                <ul className="mt-1 flex flex-col gap-1 pl-1">
                  {ingredientRows.map((ing, ii) => {
                    const token = ingredientRefToken(ing);
                    const named = ing.name.trim() !== '';
                    const inputId = `step-${row.key}-uses-${ing.key}`;
                    return (
                      <li key={ing.key}>
                        <label
                          htmlFor={inputId}
                          className={`flex min-h-9 items-center gap-2 text-sm ${
                            named ? 'text-ink' : 'text-muted opacity-60'
                          }`}
                        >
                          <input
                            id={inputId}
                            type="checkbox"
                            disabled={!named}
                            checked={row.refIngredientIds.includes(token)}
                            onChange={(e) => toggleUses(i, token, e.target.checked)}
                            className="size-4 accent-[var(--color-brand)]"
                          />
                          {named ? ing.name : `Ingredient ${ii + 1} (no name yet)`}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </details>
            ) : null}
          </li>
        ))}
      </ol>

      <button
        type="button"
        onClick={() => onChange([...rows, emptyStepRow()])}
        className="min-h-11 self-start rounded-lg border border-line px-3 text-sm font-semibold text-brand"
      >
        Add step
      </button>
    </div>
  );
}
